import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Save, Loader2, Key, Database, Shield, Mail } from 'lucide-react';
import { useAuth } from '../../AuthContext';
import { settingsApi, credentialsApi, ApiError } from '../../lib/api';

export default function IntegrationsTab() {
  const { currentUser: user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savingTradier, setSavingTradier] = useState(false);
  const [savingAlpaca, setSavingAlpaca] = useState(false);
  const [savingEodhd, setSavingEodhd] = useState(false);
  const [savingOpenrouter, setSavingOpenrouter] = useState(false);
  const [brokerStatus, setBrokerStatus] = useState<any>(null);

  const [keys, setKeys] = useState({
    alpaca_key: '',
    alpaca_secret: '',
    tradier_token: '',
    tradier_account_id: '',
    openrouter_key: '',
    eodhd_key: '',
  });
  /** Matches Titan credential store — broker APIs read this, not legacy /settings/keys Alpaca fields */
  const [alpacaPaper, setAlpacaPaper] = useState(true);

  const [smtp, setSmtp] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    from_address: '',
    use_tls: true,
  });
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpMsg, setSmtpMsg] = useState('');

  useEffect(() => { fetchKeys(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchKeys = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const [configData, smtpData, brokerStatus] = await Promise.all([
        settingsApi.getConfigs(),
        settingsApi.getSmtp().catch(() => null),
        settingsApi.brokerStatus().catch(() => null),
      ]);
      if (brokerStatus?.alpaca_mode === 'live') {
        setAlpacaPaper(false);
      } else if (brokerStatus?.alpaca_mode === 'paper') {
        setAlpacaPaper(true);
      }
      setBrokerStatus(brokerStatus);
      if (configData.status === 'success') {
        const c = configData.configs as any;
        setKeys({
          alpaca_key:          c.alpaca_key           || '',
          alpaca_secret:       c.alpaca_secret        || '',
          tradier_token:       c.tradier_token        || '',
          tradier_account_id:  c.tradier_account_id   || '',
          openrouter_key:      c.openrouter_key       || '',
          eodhd_key:           c.eodhd_key            || '',
        });
      }
      if (smtpData) {
        setSmtp({
          smtp_host:     smtpData.smtp_host    || '',
          smtp_port:     smtpData.smtp_port    || 587,
          smtp_user:     smtpData.smtp_user    || '',
          smtp_password: smtpData.smtp_password || '',
          from_address:  smtpData.from_address || '',
          use_tls:       smtpData.use_tls      ?? true,
        });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'Failed to load keys');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTradier = async () => {
    if (!user) return;
    setSavingTradier(true);
    setError('');
    setSuccess('');
    try {
      const res = await credentialsApi.saveTradier(keys.tradier_token, keys.tradier_account_id, false);
      if (res?.success) {
        setSuccess(res.message || 'Tradier credentials saved.');
      } else {
        setError(res?.message || 'Failed to save Tradier credentials');
      }
      await fetchKeys();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'Failed to save Tradier credentials');
    } finally {
      setSavingTradier(false);
    }
  };

  const handleSaveAlpaca = async () => {
    if (!user) return;
    const k = keys.alpaca_key.trim();
    const s = keys.alpaca_secret.trim();
    if (!k || !s) {
      setError('Enter both Alpaca API key and secret.');
      return;
    }
    setSavingAlpaca(true);
    setError('');
    setSuccess('');
    try {
      await credentialsApi.saveAlpaca(k, s, alpacaPaper);
      setSuccess(
        `Alpaca credentials saved (${alpacaPaper ? 'paper' : 'live'}). Controller restart is running in the background; trading will pick up the new broker shortly.`,
      );
      await fetchKeys();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'Failed to save Alpaca credentials');
    } finally {
      setSavingAlpaca(false);
    }
  };

  const handleSaveEodhd = async () => {
    if (!user) return;
    setSavingEodhd(true);
    setError('');
    setSuccess('');
    try {
      const res = await credentialsApi.saveEodhd(keys.eodhd_key);
      if (res?.success) {
        setSuccess(res.message || 'EODHD API key saved.');
      } else {
        setError(res?.message || 'Failed to save EODHD API key');
      }
      await fetchKeys();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'Failed to save EODHD API key');
    } finally {
      setSavingEodhd(false);
    }
  };

  const handleSaveOpenrouter = async () => {
    if (!user) return;
    setSavingOpenrouter(true);
    setError('');
    setSuccess('');
    try {
      const res = await credentialsApi.saveOpenRouter(keys.openrouter_key);
      if (res?.success) {
        setSuccess(res.message || 'OpenRouter API key saved.');
      } else {
        setError(res?.message || 'Failed to save OpenRouter API key');
      }
      await fetchKeys();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'Failed to save OpenRouter API key');
    } finally {
      setSavingOpenrouter(false);
    }
  };

  const handleSaveSmtp = async () => {
    setSmtpSaving(true);
    setSmtpMsg('');
    try {
      const res = await settingsApi.saveSmtp(smtp);
      setSmtpMsg(res.status === 'success' ? '✓ SMTP settings saved.' : res.message);
    } catch (err: any) {
      setSmtpMsg(err instanceof ApiError ? err.detail : 'Failed to save SMTP settings');
    } finally {
      setSmtpSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Platform Integrations</h2>
        <p className="text-sm text-muted-foreground">
          Manage your broker connections, data feeds, and AI providers. Each section saves independently — you only validate the keys you are updating.
        </p>
      </div>

      {error   && <div className="p-3 bg-red-900/20 text-red-400 border border-red-900/50 rounded">{error}</div>}
      {success && <div className="p-3 bg-green-900/20 text-green-400 border border-green-900/50 rounded">{success}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-md flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              Brokers
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4 rounded-lg border border-border/60 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-medium">Tradier</h3>
                <Button size="sm" onClick={handleSaveTradier} disabled={savingTradier}>
                  {savingTradier ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save Tradier
                </Button>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Account ID</label>
                <Input type="text" value={keys.tradier_account_id} onChange={e => setKeys({...keys, tradier_account_id: e.target.value})} placeholder="e.g. VA12345678" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Access Token</label>
                <Input type="password" value={keys.tradier_token} onChange={e => setKeys({...keys, tradier_token: e.target.value})} placeholder="••••••••••••••••" />
              </div>
              <p className="text-xs text-muted-foreground">Token must be at least 10 characters when set.</p>
            </div>

            <div className="space-y-4 rounded-lg border border-border/60 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-medium">Alpaca</h3>
                <Button size="sm" onClick={handleSaveAlpaca} disabled={savingAlpaca}>
                  {savingAlpaca ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save Alpaca
                </Button>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">API Key</label>
                <Input type="text" value={keys.alpaca_key} onChange={e => setKeys({...keys, alpaca_key: e.target.value})} placeholder="e.g. PKXXXXX..." />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">API Secret</label>
                <Input type="password" value={keys.alpaca_secret} onChange={e => setKeys({...keys, alpaca_secret: e.target.value})} placeholder="••••••••••••••••" />
              </div>
              <div className="flex items-center gap-3 pt-1">
                <Switch checked={alpacaPaper} onCheckedChange={setAlpacaPaper} id="alpaca-paper" />
                <label htmlFor="alpaca-paper" className="text-sm text-muted-foreground cursor-pointer">
                  Paper trading (recommended — uses Alpaca paper API)
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                Stored in the same broker credential vault used by live positions and order APIs (not only legacy system secrets).
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-md flex items-center gap-2">
                  <Database className="w-4 h-4 text-primary" />
                  Data Providers
                </CardTitle>
                <Button size="sm" onClick={handleSaveEodhd} disabled={savingEodhd}>
                  {savingEodhd ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save EODHD
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">EODHD API Key</label>
                <Input type="password" value={keys.eodhd_key} onChange={e => setKeys({...keys, eodhd_key: e.target.value})} placeholder="••••••••••••••••" />
                <p className="text-xs text-muted-foreground mt-1">Required for pre-market scans and historical data. Min 10 characters.</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-md flex items-center gap-2">
                  <Key className="w-4 h-4 text-primary" />
                  AI Providers
                </CardTitle>
                <Button size="sm" onClick={handleSaveOpenrouter} disabled={savingOpenrouter}>
                  {savingOpenrouter ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save OpenRouter
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">OpenRouter API Key</label>
                <Input type="password" value={keys.openrouter_key} onChange={e => setKeys({...keys, openrouter_key: e.target.value})} placeholder="sk-or-v1-..." />
                <p className="text-xs text-muted-foreground mt-1">Routes all AI requests to OpenRouter models. Min 10 characters.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* SMTP / Email Notifications */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-md flex items-center gap-2">
              <Mail className="w-4 h-4 text-primary" />
              Email Notifications (SMTP)
            </CardTitle>
            <Button size="sm" onClick={handleSaveSmtp} disabled={smtpSaving}>
              {smtpSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save SMTP
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Used for War Room email briefings. Works with Gmail App Passwords (smtp.gmail.com:587) or any SMTP provider.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {smtpMsg && (
            <div className={`p-2 text-xs rounded border ${smtpMsg.startsWith('✓') ? 'bg-green-900/20 text-green-400 border-green-900/50' : 'bg-red-900/20 text-red-400 border-red-900/50'}`}>
              {smtpMsg}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">SMTP Host</label>
              <Input placeholder="smtp.gmail.com" value={smtp.smtp_host} onChange={e => setSmtp({...smtp, smtp_host: e.target.value})} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Port</label>
              <Input type="number" placeholder="587" value={smtp.smtp_port} onChange={e => setSmtp({...smtp, smtp_port: parseInt(e.target.value) || 587})} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Username / Email</label>
              <Input placeholder="you@gmail.com" value={smtp.smtp_user} onChange={e => setSmtp({...smtp, smtp_user: e.target.value})} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Password / App Password</label>
              <Input type="password" placeholder="••••••••••••••••" value={smtp.smtp_password} onChange={e => setSmtp({...smtp, smtp_password: e.target.value})} />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground mb-1 block">From Address (optional — defaults to username)</label>
              <Input placeholder="Titan-X Alerts <you@gmail.com>" value={smtp.from_address} onChange={e => setSmtp({...smtp, from_address: e.target.value})} />
            </div>
          </div>
          <div className="flex items-center gap-3 pt-1">
            <Switch checked={smtp.use_tls} onCheckedChange={v => setSmtp({...smtp, use_tls: v})} />
            <label className="text-sm text-muted-foreground">Use TLS (required for Gmail / most providers)</label>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
